import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { TileFiltersProvider } from '@/catalog-apps/hooks/useTileFilters'
import { Loader } from '@/components/prompt-kit/loader'
import { Badge } from '@/components/shadcn/badge'
import { ENDPOINTS } from '@/util'
import { useQuery } from '@tanstack/react-query'
import { useRef } from 'react'
import { ChartBarContent } from '../component-library/ChartBar/ChartBarContent'
import { ChartBarEmptyResponse } from '../component-library/ChartBar/ChartBarEmptyResponse'
import { isRenderableChartBarResponse } from '../component-library/ChartBar/chartBarUtils'
import type { ChartBarResponse } from '../component-library/ChartBar/types'
import { ChartContentV2 } from '../component-library/ChartComponent/ChartContentV2'
import type { ChartResponse } from '../component-library/ChartComponent/types-v2'
import { ChartDumbbellContent } from '../component-library/ChartDumbbell/ChartDumbbellContent'
import type { ChartDumbbellResponse } from '../component-library/ChartDumbbell/types'
import { DataFormContent } from '../component-library/DataFormComponent/DataFormContent'
import type { DataFormResponse } from '../component-library/DataFormComponent/types'
import { populateDefaultFormValues } from '../component-library/DataFormComponent/utils'
import { DataTableV2Content } from '../component-library/DataTableV2/DataTableV2Content'
import type { DataTableV2Response } from '../component-library/DataTableV2/types'
import { FormContentV2 } from '../component-library/FormComponent/FormContentV2'
import type { InferFormValues, JsonFormSchemaV2 } from '../component-library/FormComponent/types'
import { useSubmitForm } from '../component-library/FormComponent/useSubmitForm'
import { ChartHeatmapContent } from '../component-library/ChartHeatmap/ChartHeatmapContent'
import type { ChartHeatmapResponse } from '../component-library/ChartHeatmap/types-v3'
import { ChartLineContent } from '../component-library/ChartLine/ChartLineContent'
import type { ChartLineResponse } from '../component-library/ChartLine/types'
import { MdTextContent } from '../component-library/MdTextComponent/MdTextContent'
import type { MdTextResponse } from '../component-library/MdTextComponent/types'
import { ChartPieContent } from '../component-library/ChartPie/ChartPieContent'
import type { ChartPieResponse } from '../component-library/ChartPie/types'
import { ChartRadarContent } from '../component-library/ChartRadar/ChartRadarContent'
import type { ChartRadarResponse } from '../component-library/ChartRadar/types'
import { ChartRadialContent } from '../component-library/ChartRadial/ChartRadialContent'
import type { RadialChartResponse } from '../component-library/ChartRadial/types'
import { ChartSankeyContent } from '../component-library/ChartSankey/ChartSankeyContent'
import type { ChartSankeyResponse } from '../component-library/ChartSankey/types'
import { ChartScatterContent } from '../component-library/ChartScatter/ChartScatterContent'
import type { ChartScatterResponse } from '../component-library/ChartScatter/types'
import { CatalogComponentRuntimeProvider } from '../component-library/shared/CatalogComponentRuntimeContext'
import { ChatCatalogComponent, ChatCatalogRequest, JsonObject } from './types'

const UNSUPPORTED_COMPONENT_TYPES = new Set([
  'form',
  'table',
  'statement-editor',
  'chat',
  'live-chat',
  'quick-nav',
  'component-type',
])

const SUPPORTED_COMPONENT_TYPES = new Set([
  'vega-lite-chart',
  'chart-bar',
  'chart-dumbbell',
  'chart-heatmap',
  'chart-line',
  'chart-pie',
  'chart-radar',
  'chart-radial',
  'chart-sankey',
  'chart-scatter',
  'data-form',
  'data-table-v2',
  'md-text',
])

type SupportedCatalogResponse =
  | ChartBarResponse
  | ChartDumbbellResponse
  | ChartHeatmapResponse
  | ChartLineResponse
  | ChartPieResponse
  | ChartRadarResponse
  | ChartResponse
  | DataFormResponse
  | DataTableV2Response
  | MdTextResponse
  | RadialChartResponse
  | ChartSankeyResponse
  | ChartScatterResponse

function isSupportedComponentType(componentType: string) {
  return SUPPORTED_COMPONENT_TYPES.has(componentType)
}

function isRecord(value: unknown): value is JsonObject {
  return value !== null && typeof value === 'object'
}

function formatFilterValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((entry) => formatFilterValue(entry)).join(', ')
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }
  if (value === null || value === undefined) {
    return 'none'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

function getFilterLabels(filters: Record<string, unknown>[]) {
  return filters.flatMap((filter) =>
    Object.entries(filter).map(([key, value]) => `${key}: ${formatFilterValue(value)}`)
  )
}

function resolveCatalogEndpointUrl(endpoint: string): string {
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint
  }

  if (endpoint.startsWith('/rest/')) {
    return `${ENDPOINTS.DOTNET}${endpoint}`
  }

  if (endpoint.startsWith('/catalog/')) {
    return `${ENDPOINTS.DOTNET}/rest${endpoint}`
  }

  if (endpoint.startsWith('/')) {
    return `${ENDPOINTS.CC_DOTNET}${endpoint}`
  }

  return `${ENDPOINTS.CC_DOTNET}/${endpoint}`
}

function withContainerSizedVegaSpec(response: SupportedCatalogResponse): ChartResponse {
  const chartResponse = response as ChartResponse
  const uiSchema: JsonObject = isRecord(chartResponse.uiSchema) ? chartResponse.uiSchema : {}
  const spec: JsonObject = isRecord(uiSchema.spec) ? uiSchema.spec : {}

  return {
    ...chartResponse,
    uiSchema: {
      ...uiSchema,
      spec: {
        ...spec,
        width: 'container',
        height: 'container',
      },
    },
  } as ChartResponse
}

async function fetchCatalogComponentData({
  endpoint,
  appKey,
  projectId,
  handlerId,
  filters,
  payload,
}: {
  endpoint: string
  appKey: string
  projectId: number
  handlerId: string
  filters: Record<string, unknown>[]
  payload?: JsonObject
}) {
  const tokenRaw = sessionStorage.getItem('token')
  const token = tokenRaw ? (JSON.parse(tokenRaw) as string | null) : null

  if (!token) {
    throw new Error('No token found')
  }

  const response = await fetch(resolveCatalogEndpointUrl(endpoint), {
    method: 'POST',
    headers: {
      'Content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(
      payload
        ? {
            app_id: appKey,
            project_id: projectId,
            content_id: handlerId,
            filters,
            payload,
          }
        : {
            app_id: appKey,
            project_id: projectId,
            content_id: handlerId,
            filters,
          }
    ),
  })

  const data = (await response.json()) as SupportedCatalogResponse
  if (!response.ok) {
    throw new Error('Failed to fetch catalog component')
  }

  return data
}

function useChatCatalogComponentData(request: ChatCatalogRequest) {
  const { appKey, projectId } = useAppContext()
  const filters = request.filters ?? []
  const payload = request.payload
  const canFetchLive = Boolean(appKey && projectId && request.endpoint && request.handlerId && request.componentType)

  const query = useQuery({
    queryKey: [
      request.handlerId ?? 'missing-handler',
      request.componentType ?? 'missing-component-type',
      'ai-chat-catalog-component',
      appKey ?? 'missing-app',
      projectId ?? 'missing-project',
      filters,
      payload,
    ],
    queryFn: () =>
      fetchCatalogComponentData({
        endpoint: request.endpoint ?? '',
        appKey: appKey ?? '',
        projectId: projectId ?? -1,
        handlerId: request.handlerId ?? '',
        filters,
        payload,
      }),
    enabled: canFetchLive,
  })

  if (canFetchLive && query.data) {
    return {
      data: query.data,
      filters,
      isLoading: false,
      isRefetching: query.isRefetching,
      refetch: query.refetch,
      error: null,
    }
  }

  if (canFetchLive && query.isPending) {
    return {
      data: null,
      filters,
      isLoading: true,
      isRefetching: false,
      refetch: query.refetch,
      error: null,
    }
  }

  return {
    data: null,
    filters,
    isLoading: false,
    isRefetching: false,
    refetch: query.refetch,
    error: query.error instanceof Error ? query.error : null,
  }
}

function getComponentHeightClass(componentType: string) {
  switch (componentType) {
    case 'data-table-v2':
      return 'min-h-[34rem]'
    case 'md-text':
      return 'min-h-[30rem]'
    case 'data-form':
      return 'min-h-[24rem]'
    default:
      return 'min-h-[26rem]'
  }
}

function CatalogMetaBadges({ filters, capturedAt }: { filters: Record<string, unknown>[]; capturedAt?: string }) {
  const filterLabels = getFilterLabels(filters)

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Badge>Live</Badge>
      {capturedAt ? <Badge variant="outline">{capturedAt}</Badge> : null}
      {filterLabels.map((label) => (
        <Badge key={label} variant="outline">
          {label}
        </Badge>
      ))}
    </div>
  )
}

function UnsupportedComponentCard({ componentType }: { componentType: string }) {
  const description = UNSUPPORTED_COMPONENT_TYPES.has(componentType)
    ? 'This component type is intentionally disabled in chat.'
    : 'This component type is not supported in chat yet.'

  return (
    <div className="rounded-xl border bg-background/80 p-4">
      <div className="text-sm font-medium">{componentType}</div>
      <div className="mt-1 text-sm text-muted-foreground">{description}</div>
    </div>
  )
}

function LoadingComponentCard({ componentType }: { componentType: string }) {
  return (
    <div className="flex min-h-[16rem] items-center justify-center rounded-xl border bg-background/80 p-4">
      <Loader variant="typing" size="md" />
      <span className="ml-3 text-sm text-muted-foreground">Loading live {componentType}...</span>
    </div>
  )
}

function ComponentErrorCard({ componentType, errorMessage }: { componentType: string; errorMessage: string }) {
  return (
    <div className="rounded-xl border bg-background/80 p-4">
      <div className="text-sm font-medium">{componentType}</div>
      <div className="mt-1 text-sm text-muted-foreground">{errorMessage}</div>
    </div>
  )
}

function ChatDataFormRenderer({
  response,
  meta,
  sourceFilters,
}: {
  response: DataFormResponse
  meta: React.ReactNode
  sourceFilters: Record<string, unknown>[]
}) {
  const dataFormResponse = populateDefaultFormValues(response)
  const handlerId = response.uiSchema.actions?.handlerId ?? ''
  const { mutateAsync: submitForm, isPending: isSubmitting } = useSubmitForm(handlerId, sourceFilters)

  if (dataFormResponse.uiSchema.dataDisplay.type === 'none') {
    const submitHandler = (formData: InferFormValues<JsonFormSchemaV2>) => {
      void submitForm(formData as Record<string, unknown>)
    }

    return (
      <FormContentV2
        schema={dataFormResponse.uiSchema.schema}
        submitHandler={submitHandler}
        tileFilters={meta}
        isSubmitting={isSubmitting}
      />
    )
  }

  return (
    <DataFormContent
      tileFilters={meta}
      uiSchema={dataFormResponse.uiSchema}
      formValues={dataFormResponse.dataSchema.formData ?? {}}
    />
  )
}

function ChatCatalogComponentContent({
  request,
  response,
  meta,
  onReload,
  isRefetching,
}: {
  request: ChatCatalogRequest
  response: SupportedCatalogResponse
  meta: React.ReactNode
  onReload: () => void
  isRefetching: boolean
}) {
  const tileContainerRef = useRef<HTMLDivElement>(null)

  switch (request.componentType) {
    case 'vega-lite-chart':
      return (
        <div ref={tileContainerRef} className="h-full">
          <ChartContentV2
            data={withContainerSizedVegaSpec(response)}
            tileFilters={meta}
            tileContainerRef={tileContainerRef}
          />
        </div>
      )
    case 'chart-bar': {
      const chartBarResponse = response as ChartBarResponse
      if (!isRenderableChartBarResponse(chartBarResponse)) {
        return (
          <ChartBarEmptyResponse
            title={chartBarResponse.uiSchema?.title}
            description={chartBarResponse.uiSchema?.description}
            emphasizeHeader={chartBarResponse.uiSchema?.emphasizeHeader}
            tileFilters={meta}
            reloadHandler={onReload}
          />
        )
      }
      return (
        <ChartBarContent
          uiSchema={chartBarResponse.uiSchema}
          dataSchema={chartBarResponse.dataSchema}
          tileFilters={meta}
          reloadHandler={onReload}
        />
      )
    }
    case 'chart-dumbbell': {
      const chartDumbbellResponse = response as ChartDumbbellResponse
      return (
        <ChartDumbbellContent
          uiSchema={chartDumbbellResponse.uiSchema}
          dataSchema={chartDumbbellResponse.dataSchema}
          tileFilters={meta}
        />
      )
    }
    case 'chart-heatmap': {
      const chartHeatmapResponse = response as ChartHeatmapResponse
      return (
        <ChartHeatmapContent
          uiSchema={chartHeatmapResponse.uiSchema}
          dataSchema={chartHeatmapResponse.dataSchema}
          tileFilters={meta}
        />
      )
    }
    case 'chart-line': {
      const chartLineResponse = response as ChartLineResponse
      return (
        <ChartLineContent
          uiSchema={chartLineResponse.uiSchema}
          dataSchema={chartLineResponse.dataSchema}
          tileFilters={meta}
        />
      )
    }
    case 'chart-pie': {
      const chartPieResponse = response as ChartPieResponse
      return (
        <ChartPieContent
          uiSchema={chartPieResponse.uiSchema}
          dataSchema={chartPieResponse.dataSchema}
          tileFilters={meta}
        />
      )
    }
    case 'chart-radar': {
      const chartRadarResponse = response as ChartRadarResponse
      return (
        <ChartRadarContent
          uiSchema={chartRadarResponse.uiSchema}
          dataSchema={chartRadarResponse.dataSchema}
          tileFilters={meta}
        />
      )
    }
    case 'chart-radial': {
      const chartRadialResponse = response as RadialChartResponse
      return (
        <ChartRadialContent
          uiSchema={chartRadialResponse.uiSchema}
          dataSchema={chartRadialResponse.dataSchema}
          tileFilters={meta}
        />
      )
    }
    case 'chart-sankey': {
      const chartSankeyResponse = response as ChartSankeyResponse
      return (
        <ChartSankeyContent
          uiSchema={chartSankeyResponse.uiSchema}
          dataSchema={chartSankeyResponse.dataSchema}
          tileFilters={meta}
        />
      )
    }
    case 'chart-scatter': {
      const chartScatterResponse = response as ChartScatterResponse
      return (
        <ChartScatterContent
          uiSchema={chartScatterResponse.uiSchema}
          dataSchema={chartScatterResponse.dataSchema}
          tileFilters={meta}
        />
      )
    }
    case 'data-form':
      return (
        <ChatDataFormRenderer
          response={response as DataFormResponse}
          meta={meta}
          sourceFilters={request.filters ?? []}
        />
      )
    case 'data-table-v2': {
      const dataTableResponse = response as DataTableV2Response
      return (
        <DataTableV2Content
          uiSchema={dataTableResponse.uiSchema}
          dataSchema={dataTableResponse.dataSchema}
          tileFilters={meta}
          reloadHandler={onReload}
          isRefetching={isRefetching}
        />
      )
    }
    case 'md-text': {
      const mdTextResponse = response as MdTextResponse
      return (
        <MdTextContent
          uiSchema={mdTextResponse.uiSchema}
          dataSchema={mdTextResponse.dataSchema}
          tileFilters={meta}
          reloadHandler={onReload}
        />
      )
    }
    default:
      return <UnsupportedComponentCard componentType={request.componentType ?? 'unknown-component'} />
  }
}

function ChatCatalogComponentCard({
  component,
  request,
}: {
  component?: ChatCatalogComponent
  request: ChatCatalogRequest
}) {
  const { data, isLoading, isRefetching, refetch, error, filters } = useChatCatalogComponentData(request)
  const componentType = request.componentType ?? component?.componentType ?? 'unknown-component'

  if (!isSupportedComponentType(componentType)) {
    return <UnsupportedComponentCard componentType={componentType} />
  }

  if (isLoading) {
    return <LoadingComponentCard componentType={componentType} />
  }

  if (!data) {
    const errorMessage =
      request.endpoint && request.handlerId
        ? (error?.message ?? 'Failed to fetch this component.')
        : 'No request metadata was available to fetch this component.'

    return <ComponentErrorCard componentType={componentType} errorMessage={errorMessage} />
  }

  const meta = <CatalogMetaBadges filters={filters} capturedAt={component?.capturedAt} />

  return (
    <CatalogComponentRuntimeProvider
      value={{
        pageFilterSelectionsOverride: [],
        allowRouteNavigation: false,
      }}
    >
      <TileFiltersProvider initialSelections={filters} allowChanges={false}>
        <div
          className={`overflow-hidden rounded-xl border bg-background/80 p-4 shadow-sm ${getComponentHeightClass(componentType)}`}
        >
          <ChatCatalogComponentContent
            request={request}
            response={data}
            meta={meta}
            onReload={() => {
              void refetch()
            }}
            isRefetching={isRefetching}
          />
        </div>
      </TileFiltersProvider>
    </CatalogComponentRuntimeProvider>
  )
}

export function ChatCatalogComponents({
  components,
  requests,
}: {
  components: ChatCatalogComponent[]
  requests: ChatCatalogRequest[]
}) {
  if (requests.length === 0) {
    return null
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {requests.map((request, index) => {
        const matchingComponent = components[index]
        const key = `${request.componentType ?? 'unknown-component'}:${request.handlerId ?? index}:${index}`
        return <ChatCatalogComponentCard key={key} component={matchingComponent} request={request} />
      })}
    </div>
  )
}
